import React, { useState } from 'react';
import Camera from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';
import { Camera as CameraIcon, X, Upload, RotateCcw } from 'lucide-react';

interface ProductCameraProps {
  onCapture: (dataUri: string) => void;
  onClose: () => void;
  isOpen: boolean;
}

const ProductCamera: React.FC<ProductCameraProps> = ({ onCapture, onClose, isOpen }) => {
  const [capturedImage, setCapturedImage] = useState<string>('');

  const handleTakePhoto = (dataUri: string) => {
    setCapturedImage(dataUri);
  };

  const handleConfirm = () => {
    if (capturedImage) {
      onCapture(capturedImage);
      setCapturedImage('');
      onClose();
    }
  };

  const handleRetake = () => {
    setCapturedImage('');
  };

  const handleClose = () => {
    setCapturedImage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center">
            <CameraIcon className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Foto Produk</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Camera/Preview Area */}
        <div className="bg-black">
          {!capturedImage ? (
            <div className="relative">
              <Camera
                onTakePhoto={handleTakePhoto}
                idealFacingMode="environment"
                isImageMirror={false}
                imageType="jpg"
                imageCompression={0.8}
              />
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-white text-sm bg-black bg-opacity-50 inline-block px-4 py-2 rounded-full">
                  Posisikan produk di tengah layar
                </p>
              </div>
            </div>
          ) : (
            <div className="relative">
              <img 
                src={capturedImage} 
                alt="Captured product" 
                className="w-full h-auto max-h-[60vh] object-contain"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          {capturedImage ? (
            <div className="flex gap-3">
              <button
                onClick={handleRetake}
                className="flex-1 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center font-medium"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Foto Ulang
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium"
              >
                <Upload className="w-5 h-5 mr-2" />
                Gunakan Foto
              </button>
            </div>
          ) : (
            <div className="text-center">
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Batal
              </button>
            </div>
          )}
        </div>

        {/* Tips */}
        <div className="px-6 py-3 bg-blue-50 border-t border-blue-100">
          <p className="text-xs text-blue-700 text-center">
            💡 Tips: Pastikan pencahayaan cukup dan produk terlihat jelas
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductCamera;
